import { Timestamp } from '@bufbuild/protobuf/wkt'
import {
  ArrowUpRightIcon,
  BellIcon,
  CircleCheckIcon,
  CirclePlayIcon,
  DotIcon,
  LucideIcon,
} from 'lucide-react'
import { useState } from 'react'

import { useGetPortalReminderSetting } from '@/api/notifications-service.hook'
import { Company } from '@/gen/inventory/v1/company_service_pb'

import { formatTimestamp } from '@/lib/date/date'
import { cn } from '@/lib/ui/style-helpers'
import { UrlParam } from '@/lib/url/url-param.hook'
import { buildSettingsSectionUrl } from '@/lib/url/urls'

import { NotificationTab } from '@/pages/settings-page/notification-section/types'

import { Button } from '@/components/lemmala-design-system/button/button'
import { buttonVariants } from '@/components/lemmala-design-system/button/button-variants'
import { Hyperlink } from '@/components/lemmala-design-system/link/hyperlink'
import { FloatingPopover } from '@/components/lemmala-design-system/popover/floating-popover'
import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

import { SendReminderModal } from './send-reminder-modal'

interface SendReminderButtonProps {
  company: Company
}

type ReminderRowProps = {
  Icon: LucideIcon
  title: string
  label: string
  date?: Timestamp
  placeholder: string
}

const ReminderRow = ({ Icon, title, label, date, placeholder }: ReminderRowProps) => (
  <div className='flex flex-col gap-2'>
    <span className='text-main-foreground-secondary text-label-small'>{title}</span>
    {date ? (
      <div className='flex items-center gap-1'>
        <Icon className='size-4' />
        <span className='text-main-foreground-primary text-body-small'>{label}</span>
        <DotIcon strokeWidth={8} className='size-1.5 text-main-foreground-tertiary' />
        <span className='text-main-foreground-tertiary text-body-small'>
          {formatTimestamp(date, 'shortDateTime')}
        </span>
      </div>
    ) : (
      <span className='text-main-foreground-tertiary text-body-small'>{placeholder}</span>
    )}
  </div>
)

export const SendReminderButton = ({ company }: SendReminderButtonProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { data } = useGetPortalReminderSetting()

  const isRemindersEnabled = !!data?.cadenceInterval

  const handleSendManualReminderClick = () => {
    setIsPopoverOpen(false)
    setIsModalOpen(true)
  }

  return (
    <>
      <FloatingPopover
        align='start'
        className='w-60'
        showClose={false}
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        trigger={
          <Button variant='ghost-secondary' size='sm'>
            <TextWithIcon
              text={isRemindersEnabled ? 'Reminders enabled' : 'No reminders set'}
              icon={
                <span className='relative inline-flex'>
                  <BellIcon className='size-4' />
                  {isRemindersEnabled && (
                    <span
                      className='absolute right-0 size-1.125 rounded-full bg-main-foreground-success ring-1 ring-white'
                      aria-hidden
                    />
                  )}
                </span>
              }
            />
          </Button>
        }
        showCancelButton={false}
        showPrimaryButton={false}
      >
        <div className='flex flex-col gap-3 p-2'>
          {isRemindersEnabled ? (
            <>
              <ReminderRow
                Icon={CirclePlayIcon}
                title='Next reminder'
                date={company.portalNextReminderAt}
                label='Scheduled'
                placeholder='No upcoming reminders scheduled'
              />
              <ReminderRow
                Icon={CircleCheckIcon}
                title='Previous reminder'
                label='Sent'
                date={company.portalLastCadenceReminderAt}
                placeholder='No reminders sent yet'
              />
            </>
          ) : (
            <div className='flex flex-col gap-0.5'>Automatic reminders are off.</div>
          )}
          <Separator />
          {!isRemindersEnabled && (
            <Hyperlink
              className={cn(buttonVariants({ variant: 'link', size: 'sm' }), 'justify-start p-0')}
              href={buildSettingsSectionUrl('notifications', {
                [UrlParam.NOTIFICATION_TAB]: NotificationTab.THIRD_PARTY,
              })}
            >
              <TextWithIcon
                iconPosition='end'
                text='Manage reminders settings'
                icon={<ArrowUpRightIcon className='size-3.5' />}
              />
            </Hyperlink>
          )}
          <Button
            variant='link'
            size='sm'
            className='w-full justify-start p-0'
            onClick={handleSendManualReminderClick}
          >
            Send manual reminder
          </Button>
        </div>
      </FloatingPopover>
      {isModalOpen && (
        <SendReminderModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          companyId={company.id}
          recipientsEmails={company.recipientsEmails}
        />
      )}
    </>
  )
}
